<template>
  <div
    class="tw:align-center tw:flex tw:gap-4 tw:rounded-sm tw:border tw:border-solid tw:border-denim-300 tw:px-2 tw:py-4 tw:text-xs tw:font-normal"
    data-testid="manage-social-connections-card"
  >
    <Checkbox
      class="tw:h-[18px]!"
      data-testid="checkbox"
      dense
      :model-value="isChecked"
      new-design
      @update:model-value="checkboxClicked"
    />
    <div class="tw:flex tw:grow tw:gap-2 tw:min-w-0">
      <ImageIcon
        fallback-icon="account_circle"
        image-class="tw:rounded-full"
        size="42px"
        :src="handle.profilePicUrl"
      />
      <div class="tw:flex tw:flex-col tw:min-w-0">
        <LineClamp
          class="tw:denim-900 tw:line-height-[140%] tw:text-sm tw:font-bold"
          :lines="1"
          tag="h3"
        >
          {{ name }}
          <template #after="{ clamped }">
            <StreemTooltip
              v-if="clamped"
              :offset="[0, -10]"
            >
              {{ handle.fullName }}
            </StreemTooltip>
          </template>
        </LineClamp>
        <a
          class="tw:mb-1 tw:text-blue-800 tw:no-underline"
          :href="handle.url"
          rel="noopener noreferrer"
          target="_blank"
        >
          @{{ handle.username }}
        </a>
        <EllipseContent
          v-if="handle.website"
          as="a"
          class="tw:mb-1 tw:text-blue-800 tw:no-underline"
          :href="handle.website"
          rel="noopener noreferrer"
          target="_blank"
        >
          {{ handle.website }}
        </EllipseContent>
      </div>
    </div>
    <div class="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-2">
      <span class="tw:denim-900 tw:block tw:text-sm tw:font-bold">{{
        followerCount.pretty
      }}</span>
      <span class="tw:leading-none">{{
        getLocalePluralText("global.followers", followerCount.count)
      }}</span>
    </div>
    <div class="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-2">
      <span class="tw:denim-900 tw:block tw:text-sm tw:font-bold">{{
        mediaCount.pretty
      }}</span>
      <span class="tw:leading-none">{{
        getLocalePluralText("global.posts", mediaCount.count)
      }}</span>
    </div>
    <div>
      <BaseButton
        :aria-label="getLocaleText('global.delete')"
        class="tw:-mt-2 tw:text-blue-900"
        data-testid="delete-button"
        flat
        icon="delete_outline"
        icon-size="26px"
        new-design
        @click="emits('delete-clicked', props.id)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { type UnwrapRef, computed } from "vue";

import { getLocalePluralText, getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  EllipseContent,
  ImageIcon,
  LineClamp,
  StreemTooltip,
} from "shared/components/base";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import { prettyNumber } from "shared/helpers/number";
import { type InstagramHandlesOrganisationSocialIntegration } from "shared/resources";
import type InstagramHandle from "shared/resources/InstagramHandle";

type Id = NonNullable<InstagramHandlesOrganisationSocialIntegration["id"]>;
interface ManageSocialConnectionsCardProps {
  handle: UnwrapRef<InstagramHandle>;
  id: Id;
  isChecked?: boolean;
}

const props = defineProps<ManageSocialConnectionsCardProps>();

const mediaCount = computed(() => {
  const count = props.handle.mediaCount ?? 0;

  return {
    pretty: prettyNumber(count, "suffix"),
    count,
  };
});

const followerCount = computed(() => {
  const count = props.handle.followerCount ?? 0;

  return {
    pretty: prettyNumber(count, "suffix"),
    count,
  };
});

const name = computed(() => props.handle.fullName ?? props.handle.username);

const emits = defineEmits<{
  "checkbox-clicked": [Id, boolean];
  "delete-clicked": [Id];
}>();

function checkboxClicked(checked: boolean): void {
  emits("checkbox-clicked", props.id, checked);
}
</script>
